import { Menu as BaseMenu } from '@base-ui/react/menu';
import { Check, ChevronRight } from 'lucide-react';
import type { ComponentProps, ReactElement, ReactNode } from 'react';
import { cn } from '../../lib/cn';

/** Выпадающее меню в духе shadcn DropdownMenu на Base UI (docs/saas/screens/схема.md). */
export const Menu = BaseMenu.Root;

/** Триггер меню: свой элемент (кнопка, иконка) через `render`. */
export function MenuTrigger({ children }: { children: ReactElement }) {
  return <BaseMenu.Trigger render={children} />;
}

const POPUP =
  'min-w-52 overflow-hidden rounded-lg border border-border bg-card p-1 text-sm text-foreground shadow-lg outline-none transition-[opacity,transform] data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0';

interface MenuContentProps {
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  className?: string;
}

export function MenuContent({
  children,
  side = 'bottom',
  align = 'start',
  className,
}: MenuContentProps) {
  return (
    <BaseMenu.Portal>
      <BaseMenu.Positioner side={side} align={align} sideOffset={6} className="z-50">
        <BaseMenu.Popup className={cn(POPUP, className)}>{children}</BaseMenu.Popup>
      </BaseMenu.Positioner>
    </BaseMenu.Portal>
  );
}

const ITEM =
  'relative flex cursor-default items-center gap-2.5 rounded-md px-2.5 py-2 outline-none select-none data-[disabled]:pointer-events-none data-[disabled]:opacity-50 data-[highlighted]:bg-accent data-[highlighted]:text-accent-foreground [&_svg]:size-4 [&_svg]:shrink-0';

interface MenuItemProps extends Omit<ComponentProps<typeof BaseMenu.Item>, 'className'> {
  /** Разрушительное действие (удалить): красный текст. */
  danger?: boolean;
  className?: string;
}

export function MenuItem({ danger = false, className, ...props }: MenuItemProps) {
  return (
    <BaseMenu.Item
      className={cn(
        ITEM,
        danger &&
          'text-danger data-[highlighted]:bg-danger/10 data-[highlighted]:text-danger [&_svg]:text-danger',
        className,
      )}
      {...props}
    />
  );
}

/** Пункт с галочкой: выбранная схема, текущий чат, язык. */
export function MenuCheckboxItem({
  children,
  className,
  ...props
}: Omit<ComponentProps<typeof BaseMenu.CheckboxItem>, 'className'> & { className?: string }) {
  return (
    <BaseMenu.CheckboxItem className={cn(ITEM, 'pr-8', className)} {...props}>
      {children}
      <BaseMenu.CheckboxItemIndicator className="ml-auto -mr-6 flex">
        <Check className="text-foreground" />
      </BaseMenu.CheckboxItemIndicator>
    </BaseMenu.CheckboxItem>
  );
}

export const MenuRadioGroup = BaseMenu.RadioGroup;

export function MenuRadioItem({
  children,
  className,
  ...props
}: Omit<ComponentProps<typeof BaseMenu.RadioItem>, 'className'> & { className?: string }) {
  return (
    <BaseMenu.RadioItem className={cn(ITEM, 'pr-8', className)} {...props}>
      {children}
      <BaseMenu.RadioItemIndicator className="ml-auto -mr-6 flex">
        <Check className="text-foreground" />
      </BaseMenu.RadioItemIndicator>
    </BaseMenu.RadioItem>
  );
}

export function MenuSeparator() {
  return <BaseMenu.Separator className="mx-1 my-1 h-px bg-border" />;
}

export function MenuGroup({ label, children }: { label?: string; children: ReactNode }) {
  return (
    <BaseMenu.Group>
      {label !== undefined && (
        <BaseMenu.GroupLabel className="px-2.5 py-1.5 text-xs font-medium text-muted-foreground">
          {label}
        </BaseMenu.GroupLabel>
      )}
      {children}
    </BaseMenu.Group>
  );
}

/** Вложенное меню: пункт со стрелкой, содержимое открывается сбоку. */
export function MenuSubmenu({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <BaseMenu.SubmenuRoot>
      <BaseMenu.SubmenuTrigger className={cn(ITEM, 'data-[popup-open]:bg-tint')}>
        {label}
        <ChevronRight className="ml-auto" />
      </BaseMenu.SubmenuTrigger>
      <MenuContent side="right" align="start">
        {children}
      </MenuContent>
    </BaseMenu.SubmenuRoot>
  );
}
